import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import './AdminDashboard.css';
import BlogEditor from '../components/BlogEditor';

const API = '/api/admin.php';

const CONTACT_STATUSES = ['new', 'reviewed', 'contacted', 'converted'];
const APP_STATUSES     = ['new', 'reviewed', 'shortlisted', 'rejected'];
const LEAD_STATUSES    = ['new', 'contacted', 'converted', 'spam'];
const DEPARTMENTS      = ['marketing', 'webdev', 'marketplace', 'content', 'design', 'operations'];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso || '—'; }
}
function fmtDateTime(iso) {
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso || '—'; }
}

function SortTh({ field, config, onSort, children, className }) {
  return (
    <th className={`db-th--sortable${className ? ' ' + className : ''}`} onClick={() => onSort(field)}>
      {children}
      {config.field === field
        ? <span className="db-sort-icon">{config.dir === 'asc' ? ' ↑' : ' ↓'}</span>
        : <span className="db-sort-icon db-sort-icon--inactive"> ↕</span>}
    </th>
  );
}

function exportCSV(rows, headers, fields, filename) {
  const csv = [
    headers.map(h => `"${h}"`).join(','),
    ...rows.map(r => fields.map(f => `"${(r[f] ?? '').toString().replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename + '_' + new Date().toISOString().slice(0, 10) + '.csv';
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const IcoGrid = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);
const IcoMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="3"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);
const IcoBriefcase = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    <line x1="12" y1="12" x2="12" y2="12"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
  </svg>
);
const IcoZap = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const IcoBook = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);
const generateSlug = (title) =>
  title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 100);

const EMPTY_BLOG = {
  id: null, title: '', slug: '', excerpt: '', category: '', category_color: '#4f46e5',
  cover_image: '', author: 'MMC Team', read_time: '5 min read', status: 'draft', body: '',
  meta_title: '', meta_description: '', meta_keywords: '', focus_keyword: '',
  og_image: '', canonical_url: '', published_at: '',
};
const IcoRefresh = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/>
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);
const IcoChevron = ({ right }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    {right ? <polyline points="9 18 15 12 9 6"/> : <polyline points="15 18 9 12 15 6"/>}
  </svg>
);

export default function AdminDashboard() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const [token,     setToken]     = useState(() => sessionStorage.getItem('mmc_admin') || '');
  const [adminUser, setAdminUser] = useState(() => sessionStorage.getItem('mmc_admin_user') || '');
  const [username,  setUsername]  = useState('');
  const [pwd,       setPwd]       = useState('');
  const [loginErr,  setLoginErr]  = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // ── Change-password modal ─────────────────────────────────────────────────
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [cpOld, setCpOld]   = useState('');
  const [cpNew, setCpNew]   = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpErr, setCpErr]   = useState('');
  const [cpSuccess, setCpSuccess] = useState('');
  const [cpWorking, setCpWorking] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [contacts,     setContacts]     = useState([]);
  const [applications, setApplications] = useState([]);
  const [leads,        setLeads]        = useState([]);
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');

  // ── Navigation ────────────────────────────────────────────────────────────
  const [view,      setView]      = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expanded,  setExpanded]  = useState(null);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState('');
  const [deptFilter,   setDeptFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [sortConfig,   setSortConfig]   = useState({ field: 'created_at', dir: 'desc' });

  // ── Per-row actions ───────────────────────────────────────────────────────
  const [downloading,    setDownloading]    = useState(null);
  const [deleting,       setDeleting]       = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const [selected,    setSelected]    = useState(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);

  // ── Notes ─────────────────────────────────────────────────────────────────
  const [notes,      setNotes]      = useState({});
  const [noteInput,  setNoteInput]  = useState({});
  const [savingNote, setSavingNote] = useState(null);

  // ── Blogs ─────────────────────────────────────────────────────────────────
  const [blogs,           setBlogs]           = useState([]);
  const [blogLoading,     setBlogLoading]     = useState(false);
  const [blogError,       setBlogError]       = useState('');
  const [editingBlog,     setEditingBlog]     = useState(null);
  const [blogSaving,      setBlogSaving]      = useState(false);
  const [blogSaveErr,     setBlogSaveErr]     = useState('');
  const [blogSearch,      setBlogSearch]      = useState('');
  const [blogStatusFilter,setBlogStatusFilter]= useState('');
  const [seoOpen,         setSeoOpen]         = useState(false);
  const [coverUploading,  setCoverUploading]  = useState(false);
  const [coverUploadErr,  setCoverUploadErr]  = useState('');
  const coverImgRef = useRef(null);

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const h = authHeader();
      const [sRes, cRes, aRes, lRes] = await Promise.all([
        fetch(`${API}?action=stats`,        { headers: h }),
        fetch(`${API}?action=contacts`,     { headers: h }),
        fetch(`${API}?action=applications`, { headers: h }),
        fetch(`${API}?action=leads`,        { headers: h }),
      ]);
      const [s, c, a, l] = await Promise.all([sRes.json(), cRes.json(), aRes.json(), lRes.json()]);
      if (s.success) setStats(s);
      if (c.success) setContacts(c.rows);
      if (a.success) setApplications(a.rows);
      if (l.success) setLeads(l.rows);
    } catch {
      setError('Failed to load data. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [token, authHeader]);

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  // ── Blog API helpers ──────────────────────────────────────────────────────
  const fetchBlogs = useCallback(async () => {
    if (!token) return;
    setBlogLoading(true); setBlogError('');
    try {
      const res  = await fetch(`${API}?action=admin_blogs`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) setBlogs(data.rows || []);
      else setBlogError(data.message || 'Failed to load posts.');
    } catch { setBlogError('Network error loading posts.'); }
    finally  { setBlogLoading(false); }
  }, [token, authHeader]);

  const saveBlog = async (form) => {
    setBlogSaving(true); setBlogSaveErr('');
    try {
      const action = form.id ? 'update_blog' : 'create_blog';
      const res  = await fetch(`${API}?action=${action}`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { await fetchBlogs(); setEditingBlog(null); setSeoOpen(false); }
      else setBlogSaveErr(data.message || 'Failed to save post.');
    } catch { setBlogSaveErr('Network error. Please try again.'); }
    finally  { setBlogSaving(false); }
  };

  const deleteBlog = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?\n\nThis cannot be undone.`)) return;
    try {
      const res  = await fetch(`${API}?action=delete_blog`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) {
        setBlogs(prev => prev.filter(b => b.id !== id));
        if (editingBlog?.id === id) { setEditingBlog(null); setSeoOpen(false); }
      } else { alert(data.message || 'Delete failed.'); }
    } catch { alert('Network error.'); }
  };

  const updateBlogForm = (field, value) =>
    setEditingBlog(prev => ({ ...prev, [field]: value }));

  // ── Auth handlers ─────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault(); setLoginErr(''); setLoggingIn(true);
    try {
      const fd = new FormData();
      fd.append('username', username); fd.append('password', pwd);
      const res  = await fetch(`${API}?action=login`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem('mmc_admin',      data.token);
        sessionStorage.setItem('mmc_admin_user', data.username);
        setToken(data.token); setAdminUser(data.username);
      } else {
        setLoginErr(data.message || 'Login failed.');
      }
    } catch { setLoginErr('Network error. Please try again.'); }
    finally  { setLoggingIn(false); }
  };

  const handleLogout = async () => {
    try {
      const fd = new FormData(); fd.append('token', token);
      await fetch(`${API}?action=logout`, { method: 'POST', body: fd, headers: authHeader() });
    } catch {}
    sessionStorage.removeItem('mmc_admin'); sessionStorage.removeItem('mmc_admin_user');
    setToken(''); setAdminUser(''); setContacts([]); setApplications([]); setLeads([]); setStats(null);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault(); setCpErr(''); setCpSuccess('');
    if (cpNew !== cpConfirm) { setCpErr('New passwords do not match.'); return; }
    if (cpNew.length < 8)    { setCpErr('New password must be at least 8 characters.'); return; }
    setCpWorking(true);
    try {
      const fd = new FormData();
      fd.append('username', adminUser); fd.append('old_password', cpOld); fd.append('new_password', cpNew);
      const res  = await fetch(`${API}?action=change_password`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) { setCpSuccess('Password changed successfully.'); setCpOld(''); setCpNew(''); setCpConfirm(''); }
      else              { setCpErr(data.message || 'Failed to change password.'); }
    } catch { setCpErr('Network error.'); }
    finally  { setCpWorking(false); }
  };

  // ── Status / delete / notes handlers ─────────────────────────────────────
  const handleStatusChange = async (type, id, status) => {
    setUpdatingStatus(id);
    try {
      const fd = new FormData();
      fd.append('type', type); fd.append('id', id); fd.append('status', status);
      const res  = await fetch(`${API}?action=update_status`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        if (type === 'contact')     setContacts(p => p.map(r => r.id === id ? { ...r, status } : r));
        if (type === 'application') setApplications(p => p.map(r => r.id === id ? { ...r, status } : r));
      }
    } catch {} finally { setUpdatingStatus(null); }
  };

  const handleLeadStatusChange = async (id, status) => {
    setUpdatingStatus(id);
    try {
      const fd = new FormData(); fd.append('id', id); fd.append('status', status);
      const res  = await fetch(`${API}?action=update_lead_status`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) setLeads(p => p.map(r => r.id === id ? { ...r, status } : r));
    } catch {} finally { setUpdatingStatus(null); }
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Delete record for "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      const fd = new FormData(); fd.append('type', type); fd.append('id', id);
      const res  = await fetch(`${API}?action=delete`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        if (type === 'contact')     setContacts(p => p.filter(r => r.id !== id));
        if (type === 'application') setApplications(p => p.filter(r => r.id !== id));
        setExpanded(null);
      }
    } catch {} finally { setDeleting(null); }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    setDeleting(id);
    try {
      const fd = new FormData(); fd.append('op', 'delete'); fd.append('ids', String(id));
      const res  = await fetch(`${API}?action=bulk_leads`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) { setLeads(p => p.filter(r => r.id !== id)); setExpanded(null); }
    } catch {} finally { setDeleting(null); }
  };

  // ── Bulk handlers ─────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} records? This cannot be undone.`)) return;
    setBulkWorking(true);
    try {
      const type = view === 'contacts' ? 'contact' : 'application';
      const fd   = new FormData(); fd.append('type', type); fd.append('ids', [...selected].join(','));
      const res  = await fetch(`${API}?action=bulk_delete`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        if (view === 'contacts')     setContacts(p => p.filter(r => !selected.has(r.id)));
        if (view === 'applications') setApplications(p => p.filter(r => !selected.has(r.id)));
        setSelected(new Set());
      }
    } catch {} finally { setBulkWorking(false); }
  };

  const handleBulkStatus = async (status) => {
    setBulkWorking(true);
    try {
      const type = view === 'contacts' ? 'contact' : 'application';
      const fd   = new FormData(); fd.append('type', type); fd.append('status', status); fd.append('ids', [...selected].join(','));
      const res  = await fetch(`${API}?action=bulk_status`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        if (view === 'contacts')     setContacts(p => p.map(r => selected.has(r.id) ? { ...r, status } : r));
        if (view === 'applications') setApplications(p => p.map(r => selected.has(r.id) ? { ...r, status } : r));
        setSelected(new Set());
      }
    } catch {} finally { setBulkWorking(false); }
  };

  const handleBulkLeads = async (op, status) => {
    if (op === 'delete' && !window.confirm(`Delete ${selected.size} leads? This cannot be undone.`)) return;
    setBulkWorking(true);
    try {
      const fd = new FormData(); fd.append('op', op); fd.append('ids', [...selected].join(','));
      if (status) fd.append('status', status);
      const res  = await fetch(`${API}?action=bulk_leads`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        if (op === 'delete') setLeads(p => p.filter(r => !selected.has(r.id)));
        else                 setLeads(p => p.map(r => selected.has(r.id) ? { ...r, status } : r));
        setSelected(new Set());
      }
    } catch {} finally { setBulkWorking(false); }
  };

  // ── Notes handlers ────────────────────────────────────────────────────────
  const fetchNotes = async (type, id) => {
    const key = `${type}:${id}`;
    if (notes[key]) return;
    setNotes(p => ({ ...p, [key]: null }));
    try {
      const res  = await fetch(`${API}?action=get_notes&type=${encodeURIComponent(type)}&record_id=${encodeURIComponent(id)}`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) setNotes(p => ({ ...p, [key]: data.rows }));
    } catch { setNotes(p => ({ ...p, [key]: [] })); }
  };

  const handleAddNote = async (type, id, key) => {
    const text = (noteInput[key] || '').trim();
    if (!text) return;
    setSavingNote(key);
    try {
      const fd = new FormData(); fd.append('type', type); fd.append('record_id', id); fd.append('note', text);
      const res  = await fetch(`${API}?action=add_note`, { method: 'POST', body: fd, headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setNotes(p => ({ ...p, [key]: [...(p[key] || []), { id: data.id, note: text, created_at: data.created_at }] }));
        setNoteInput(p => ({ ...p, [key]: '' }));
      }
    } catch {} finally { setSavingNote(null); }
  };

  const handleDeleteNote = async (type, id, noteId) => {
    const key = `${type}:${id}`;
    try {
      const fd = new FormData(); fd.append('id', noteId);
      await fetch(`${API}?action=delete_note`, { method: 'POST', body: fd, headers: authHeader() });
      setNotes(p => ({ ...p, [key]: (p[key] || []).filter(n => n.id !== noteId) }));
    } catch {}
  };

  // ── Notes section renderer ────────────────────────────────────────────────
  const renderNotes = (type, record) => {
    const key     = `${type}:${record.id}`;
    const noteList = notes[key];
    return (
      <div className="db-notes-section">
        <div className="db-notes-header">
          <span className="db-notes-title">Private Notes</span>
          {noteList && <span className="db-badge">{noteList.length}</span>}
        </div>
        {noteList === undefined && <p className="db-notes-loading">Loading…</p>}
        {noteList === null      && <p className="db-notes-loading">Loading…</p>}
        {noteList?.length === 0 && <p className="db-notes-empty">No notes yet.</p>}
        {noteList?.map(n => (
          <div key={n.id} className="db-note">
            <p className="db-note-text">{n.note}</p>
            <span className="db-note-meta">
              {fmtDateTime(n.created_at)}
              <button className="db-note-del" onClick={e => { e.stopPropagation(); handleDeleteNote(type, record.id, n.id); }}>✕</button>
            </span>
          </div>
        ))}
        <div className="db-note-add">
          <textarea
            className="db-note-input" placeholder="Add a private note… (Ctrl+Enter to save)" rows={2}
            value={noteInput[key] || ''}
            onChange={e => setNoteInput(p => ({ ...p, [key]: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleAddNote(type, record.id, key); }}
          />
          <button className="db-btn db-btn--ghost db-btn--xs"
            disabled={savingNote === key || !(noteInput[key] || '').trim()}
            onClick={() => handleAddNote(type, record.id, key)}>
            {savingNote === key ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    );
  };

  // ── Sorting / filtering ───────────────────────────────────────────────────
  const handleSort = (field) => setSortConfig(p => ({ field, dir: p.field === field && p.dir === 'asc' ? 'desc' : 'asc' }));
  const getSorted  = (arr) => {
    const { field, dir } = sortConfig;
    return [...arr].sort((a, b) => {
      const av = a[field] ?? '', bv = b[field] ?? '';
      const cmp = typeof av === 'string' ? av.localeCompare(bv, undefined, { numeric: true }) : av - bv;
      return dir === 'asc' ? cmp : -cmp;
    });
  };
  const q = search.toLowerCase();
  const matchDate = (r) => {
    if (!dateFrom && !dateTo) return true;
    const d = (r.created_at || '').slice(0, 10);
    return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
  };

  const filteredContacts = getSorted(contacts.filter(r =>
    (!q || [r.name, r.email, r.phone, r.service, r.budget, r.message, r.referral].some(v => (v || '').toLowerCase().includes(q))) &&
    matchDate(r) && (!statusFilter || r.status === statusFilter)
  ));
  const filteredApps = getSorted(applications.filter(r =>
    (!q || [r.name, r.email, r.whatsapp, r.role, r.department, r.experience].some(v => (v || '').toLowerCase().includes(q))) &&
    matchDate(r) && (!deptFilter || r.department === deptFilter) && (!statusFilter || r.status === statusFilter)
  ));
  const filteredLeads = getSorted(leads.filter(r =>
    (!q || [r.phone, r.email].some(v => (v || '').toLowerCase().includes(q))) &&
    matchDate(r) && (!statusFilter || r.status === statusFilter)
  ));

  const currentData = view === 'contacts' ? filteredContacts : view === 'applications' ? filteredApps : filteredLeads;
  const activeStatuses = view === 'contacts' ? CONTACT_STATUSES : view === 'applications' ? APP_STATUSES : LEAD_STATUSES;
  const hasFilters = search || deptFilter || statusFilter || dateFrom || dateTo;
  const allSelected = currentData.length > 0 && currentData.every(r => selected.has(r.id));

  const switchView = (v) => {
    setView(v); setExpanded(null); setSearch(''); setDeptFilter('');
    setStatusFilter(''); setSelected(new Set()); setSortConfig({ field: 'created_at', dir: 'desc' });
    setSidebarOpen(false);
    if (v === 'blogs') { setEditingBlog(null); setBlogSearch(''); setBlogStatusFilter(''); fetchBlogs(); }
  };

  const toggleRow = (type, id) => {
    const expanding = expanded !== id;
    setExpanded(expanding ? id : null);
    if (expanding && type !== 'lead') fetchNotes(type, id);
  };

  const clearFilters = () => { setSearch(''); setDeptFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); };

  const handleExport = () => {
    if (view === 'contacts') {
      exportCSV(filteredContacts, ['ID','Name','Email','Phone','Service','Budget','Referral','Status','Message','IP','Date'],
        ['id','name','email','phone','service','budget','referral','status','message','ip_address','created_at'], 'mmc_contacts');
    } else if (view === 'applications') {
      exportCSV(filteredApps, ['ID','Name','Email','WhatsApp','Department','Role','Experience','Portfolio','Status','Cover Letter','IP','Date'],
        ['id','name','email','whatsapp','department','role','experience','portfolio','status','message','ip_address','created_at'], 'mmc_applications');
    } else {
      exportCSV(filteredLeads, ['ID','Phone','Email','Status','IP','Date'],
        ['id','phone','email','status','ip_address','created_at'], 'mmc_popup_leads');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  LOGIN SCREEN
  // ─────────────────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="db-root">
        <div className="db-login-wrap">
          <form className="db-login-card" onSubmit={handleLogin} noValidate>
            <div className="db-login-brand">
              <span className="db-login-dot" />
              <span className="db-login-brand-text">MMC Admin</span>
            </div>
            <h1 className="db-login-title">Dashboard Login</h1>
            <p className="db-login-sub">Restricted area — authorised personnel only.</p>
            <label className="db-label" htmlFor="db-user">Username</label>
            <input id="db-user" type="text" className="db-input" placeholder="admin"
              value={username} onChange={e => setUsername(e.target.value)} autoFocus autoComplete="username" required />
            <label className="db-label" htmlFor="db-pwd">Password</label>
            <input id="db-pwd" type="password" className="db-input" placeholder="••••••••••"
              value={pwd} onChange={e => setPwd(e.target.value)} autoComplete="current-password" required />
            {loginErr && (
              <p className="db-login-err">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {loginErr}
              </p>
            )}
            <button type="submit" className="db-btn db-btn--primary" disabled={loggingIn}>
              {loggingIn ? 'Signing in…' : 'Sign In →'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  CHANGE PASSWORD MODAL
  // ─────────────────────────────────────────────────────────────────────────
  const changePwdModal = showChangePwd && (
    <div className="db-modal-overlay" onClick={() => setShowChangePwd(false)}>
      <form className="db-modal" onSubmit={handleChangePassword} onClick={e => e.stopPropagation()} noValidate>
        <h2 className="db-modal-title">Change Password</h2>
        <p className="db-modal-sub">Changing password for <strong>{adminUser}</strong></p>
        <label className="db-label">Current Password</label>
        <input type="password" className="db-input" value={cpOld} onChange={e => setCpOld(e.target.value)} autoComplete="current-password" required />
        <label className="db-label">New Password</label>
        <input type="password" className="db-input" placeholder="Min. 8 characters" value={cpNew} onChange={e => setCpNew(e.target.value)} autoComplete="new-password" required />
        <label className="db-label">Confirm New Password</label>
        <input type="password" className="db-input" value={cpConfirm} onChange={e => setCpConfirm(e.target.value)} autoComplete="new-password" required />
        {cpErr     && <p className="db-login-err">{cpErr}</p>}
        {cpSuccess && <p className="db-modal-ok">{cpSuccess}</p>}
        <div className="db-modal-actions">
          <button type="submit" className="db-btn db-btn--primary db-btn--sm" disabled={cpWorking}>
            {cpWorking ? 'Saving…' : 'Change Password'}
          </button>
          <button type="button" className="db-btn db-btn--ghost db-btn--sm"
            onClick={() => { setShowChangePwd(false); setCpErr(''); setCpSuccess(''); }}>Cancel</button>
        </div>
      </form>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  SIDEBAR NAV ITEMS
  // ─────────────────────────────────────────────────────────────────────────
  const navItems = [
    { id: 'overview',      label: 'Overview',      icon: <IcoGrid />,      badge: null },
    { id: 'contacts',      label: 'Contacts',      icon: <IcoMail />,      badge: stats?.contacts },
    { id: 'applications',  label: 'Applications',  icon: <IcoBriefcase />, badge: stats?.applications },
    { id: 'leads',         label: 'Popup Leads',   icon: <IcoZap />,       badge: stats?.leads },
    { id: 'blogs',         label: 'Blog Posts',    icon: <IcoBook />,      badge: blogs.length || null },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  //  BLOGS PAGE
  // ─────────────────────────────────────────────────────────────────────────
  const bq = blogSearch.toLowerCase();
  const filteredBlogs = blogs.filter(b =>
    (!bq || [b.title, b.slug, b.category, b.excerpt].some(v => (v || '').toLowerCase().includes(bq))) &&
    (!blogStatusFilter || b.status === blogStatusFilter)
  );

  const blogListView = (
    <>
      <div className="db-page-header">
        <h2 className="db-page-title">Blog Posts <span className="db-page-count">{blogs.length}</span></h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="db-btn db-btn--ghost" onClick={fetchBlogs} disabled={blogLoading}><IcoRefresh /></button>
          <button className="db-btn db-btn--primary" onClick={() => { setEditingBlog({ ...EMPTY_BLOG }); setSeoOpen(false); }}>
            + New Post
          </button>
        </div>
      </div>

      <div className="db-toolbar">
        <div className="db-toolbar-left">
          <div className="db-search-wrap">
            <svg className="db-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input className="db-search" type="search" placeholder="Search posts…"
              value={blogSearch} onChange={e => setBlogSearch(e.target.value)} />
          </div>
          <select className="db-filter-select" value={blogStatusFilter} onChange={e => setBlogStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          {(blogSearch || blogStatusFilter) && (
            <button className="db-btn db-btn--ghost db-btn--xs" onClick={() => { setBlogSearch(''); setBlogStatusFilter(''); }}>Clear</button>
          )}
        </div>
        <div className="db-toolbar-right">
          <span className="db-count">{filteredBlogs.length} posts</span>
        </div>
      </div>

      {blogError && <div className="db-error-banner">{blogError}</div>}
      {blogLoading && <div className="db-loading"><span className="db-spinner" /> Loading posts…</div>}

      {!blogLoading && (
        filteredBlogs.length === 0 ? (
          <div className="db-empty">
            {blogs.length === 0
              ? 'No blog posts yet. Click "+ New Post" to create your first!'
              : 'No posts match your filters.'}
          </div>
        ) : (
          <div className="db-table-wrap">
            <table className="db-table">
              <thead>
                <tr>
                  <th className="db-th--num">#</th>
                  <th style={{ minWidth: 240 }}>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBlogs.map(b => (
                  <tr key={b.id} className="db-row">
                    <td className="db-td--num">{b.id}</td>
                    <td>
                      <div className="db-td--name">{b.title}</div>
                      <div style={{ fontSize: '11px', color: '#6e7681', marginTop: 2 }}>/blog/{b.slug}</div>
                    </td>
                    <td>
                      {b.category ? (
                        <span className="db-blog-cat" style={{ background: b.category_color || '#4f46e5' }}>
                          {b.category}
                        </span>
                      ) : <span className="db-na">—</span>}
                    </td>
                    <td>
                      <span className={`db-blog-status db-blog-status--${b.status}`}>{b.status}</span>
                    </td>
                    <td className="db-td--date">{b.published_at ? fmtDate(b.published_at) : <span className="db-na">—</span>}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="db-btn db-btn--ghost db-btn--xs"
                          onClick={() => { setEditingBlog({ ...b }); setSeoOpen(false); }}>
                          Edit
                        </button>
                        {b.status === 'published' && (
                          <a href={`/blog/${b.slug}`} target="_blank" rel="noopener noreferrer"
                            className="db-btn db-btn--ghost db-btn--xs">
                            Preview ↗
                          </a>
                        )}
                        <button className="db-btn db-btn--delete db-btn--xs"
                          onClick={() => deleteBlog(b.id, b.title)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </>
  );

  const blogEditorView = editingBlog && (
    <div className="db-blog-editor">
      {/* ── Editor header ───────────────────────────────────────────────────── */}
      <div className="db-blog-editor-header">
        <button className="db-btn db-btn--ghost db-btn--xs" onClick={() => { setEditingBlog(null); setSeoOpen(false); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          All Posts
        </button>
        <h2 className="db-blog-editor-title">
          {editingBlog.id ? `Editing: ${editingBlog.title || 'Untitled'}` : 'New Blog Post'}
        </h2>
        <div className="db-blog-editor-actions">
          {blogSaveErr && <span className="db-blog-save-err">{blogSaveErr}</span>}
          <button className="db-btn db-btn--ghost db-btn--sm"
            disabled={blogSaving}
            onClick={() => saveBlog({ ...editingBlog, status: 'draft' })}>
            {blogSaving && editingBlog.status === 'draft' ? 'Saving…' : 'Save Draft'}
          </button>
          <button className="db-btn db-btn--primary db-btn--sm"
            disabled={blogSaving}
            onClick={() => saveBlog({ ...editingBlog, status: 'published' })}>
            {blogSaving && editingBlog.status === 'published' ? 'Publishing…' : (editingBlog.id ? 'Update & Publish' : 'Publish')}
          </button>
        </div>
      </div>

      {/* ── Two-column layout ────────────────────────────────────────────────── */}
      <div className="db-blog-layout">
        {/* Left: content */}
        <div className="db-blog-content-col">
          {/* Title */}
          <div className="db-blog-field-group">
            <label className="db-label">Post Title *</label>
            <input
              className="db-input db-blog-title-input"
              type="text"
              placeholder="Enter a compelling blog title…"
              value={editingBlog.title}
              onChange={e => {
                const t = e.target.value;
                updateBlogForm('title', t);
                if (!editingBlog.id) updateBlogForm('slug', generateSlug(t));
              }}
            />
          </div>

          {/* Slug */}
          <div className="db-blog-field-group">
            <label className="db-label">
              URL Slug *
              <span className="db-blog-slug-preview"> — /blog/{editingBlog.slug || 'your-slug'}</span>
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="db-input"
                type="text"
                placeholder="url-friendly-slug"
                value={editingBlog.slug}
                onChange={e => updateBlogForm('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              />
              <button type="button" className="db-btn db-btn--ghost db-btn--sm"
                onClick={() => updateBlogForm('slug', generateSlug(editingBlog.title))}>
                Generate
              </button>
            </div>
          </div>

          {/* Excerpt */}
          <div className="db-blog-field-group">
            <label className="db-label">Excerpt / Summary</label>
            <textarea
              className="db-input"
              rows={3}
              placeholder="Short description shown on the blog listing page and in SEO…"
              value={editingBlog.excerpt}
              onChange={e => updateBlogForm('excerpt', e.target.value)}
              style={{ resize: 'vertical', minHeight: 72 }}
            />
          </div>

          {/* Cover image */}
          <div className="db-blog-field-group">
            <label className="db-label">Cover Image</label>

            {/* Hidden file input */}
            <input
              ref={coverImgRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                setCoverUploading(true);
                setCoverUploadErr('');
                try {
                  const form = new FormData();
                  form.append('file', file);
                  const res  = await fetch('/api/upload.php', {
                    method: 'POST',
                    headers: authHeader(),
                    body: form,
                  });
                  const data = await res.json();
                  if (!data.success) throw new Error(data.message || 'Upload failed.');
                  updateBlogForm('cover_image', data.path);
                } catch (err) {
                  setCoverUploadErr(err.message);
                } finally {
                  setCoverUploading(false);
                }
              }}
            />

            {/* Upload button + URL input side by side */}
            <div className="db-cover-row">
              <button
                type="button"
                className="db-btn db-btn--ghost db-btn--sm db-cover-upload-btn"
                disabled={coverUploading}
                onClick={() => coverImgRef.current?.click()}
              >
                {coverUploading ? 'Uploading…' : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                    </svg>
                    Upload
                  </>
                )}
              </button>
              <span className="db-cover-or">or</span>
              <input
                className="db-input db-cover-url-input"
                type="url"
                placeholder="Paste image URL…"
                value={editingBlog.cover_image}
                onChange={e => { updateBlogForm('cover_image', e.target.value); setCoverUploadErr(''); }}
              />
            </div>

            {coverUploadErr && <p className="db-cover-err">{coverUploadErr}</p>}

            {editingBlog.cover_image && (
              <div className="db-blog-cover-preview">
                <img src={editingBlog.cover_image} alt="Cover preview" />
                <button
                  type="button"
                  className="db-cover-remove"
                  onClick={() => { updateBlogForm('cover_image', ''); setCoverUploadErr(''); }}
                  title="Remove cover image"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Rich text body */}
          <div className="db-blog-field-group">
            <label className="db-label">Content</label>
            <BlogEditor
              key={editingBlog.id ?? 'new'}
              value={editingBlog.body}
              onChange={html => updateBlogForm('body', html)}
              onReadTimeChange={rt => updateBlogForm('read_time', rt)}
              token={token}
            />
          </div>

          {/* ── SEO Section ─────────────────────────────────────────────────── */}
          <div className="db-blog-seo-section">
            <button
              type="button"
              className="db-blog-seo-toggle"
              onClick={() => setSeoOpen(o => !o)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              SEO &amp; Meta Settings
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', transform: seoOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {seoOpen && (
              <div className="db-blog-seo-body">
                {/* Meta Title */}
                <div className="db-blog-field-group">
                  <label className="db-label">
                    Meta Title
                    <span className={`db-seo-counter ${editingBlog.meta_title.length > 60 ? 'db-seo-counter--warn' : ''}`}>
                      {editingBlog.meta_title.length}/60
                    </span>
                  </label>
                  <input
                    className="db-input"
                    type="text"
                    maxLength={80}
                    placeholder={editingBlog.title || 'SEO page title (50–60 chars recommended)'}
                    value={editingBlog.meta_title}
                    onChange={e => updateBlogForm('meta_title', e.target.value)}
                  />
                </div>

                {/* Meta Description */}
                <div className="db-blog-field-group">
                  <label className="db-label">
                    Meta Description
                    <span className={`db-seo-counter ${editingBlog.meta_description.length > 160 ? 'db-seo-counter--warn' : editingBlog.meta_description.length > 140 ? 'db-seo-counter--ok' : ''}`}>
                      {editingBlog.meta_description.length}/160
                    </span>
                  </label>
                  <textarea
                    className="db-input"
                    rows={3}
                    maxLength={200}
                    placeholder={editingBlog.excerpt || 'Appears in search results (120–160 chars recommended)'}
                    value={editingBlog.meta_description}
                    onChange={e => updateBlogForm('meta_description', e.target.value)}
                    style={{ resize: 'vertical', minHeight: 72 }}
                  />
                </div>

                {/* Focus keyword + Keywords */}
                <div className="db-blog-two-col">
                  <div className="db-blog-field-group">
                    <label className="db-label">Focus Keyword</label>
                    <input
                      className="db-input"
                      type="text"
                      placeholder="e.g. shopify development pune"
                      value={editingBlog.focus_keyword}
                      onChange={e => updateBlogForm('focus_keyword', e.target.value)}
                    />
                  </div>
                  <div className="db-blog-field-group">
                    <label className="db-label">Meta Keywords</label>
                    <input
                      className="db-input"
                      type="text"
                      placeholder="keyword1, keyword2, keyword3"
                      value={editingBlog.meta_keywords}
                      onChange={e => updateBlogForm('meta_keywords', e.target.value)}
                    />
                  </div>
                </div>

                {/* OG Image + Canonical */}
                <div className="db-blog-two-col">
                  <div className="db-blog-field-group">
                    <label className="db-label">OG / Social Image URL</label>
                    <input
                      className="db-input"
                      type="url"
                      placeholder="https://… (1200×630 recommended)"
                      value={editingBlog.og_image}
                      onChange={e => updateBlogForm('og_image', e.target.value)}
                    />
                  </div>
                  <div className="db-blog-field-group">
                    <label className="db-label">Canonical URL</label>
                    <input
                      className="db-input"
                      type="url"
                      placeholder="https://madmarketingclub.com/blog/slug"
                      value={editingBlog.canonical_url}
                      onChange={e => updateBlogForm('canonical_url', e.target.value)}
                    />
                  </div>
                </div>

                {/* Google SERP preview */}
                <div className="db-seo-preview">
                  <div className="db-seo-preview-label">Google Preview</div>
                  <div className="db-seo-preview-card">
                    <div className="db-seo-preview-url">
                      madmarketingclub.com › blog › {editingBlog.slug || 'your-slug'}
                    </div>
                    <div className="db-seo-preview-title">
                      {editingBlog.meta_title || editingBlog.title || 'Post Title – Mad Marketing Club'}
                    </div>
                    <div className="db-seo-preview-desc">
                      {editingBlog.meta_description || editingBlog.excerpt || 'Meta description will appear here in search results. Write a compelling description to improve click-through rate.'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: settings panel */}
        <aside className="db-blog-settings-col">
          <div className="db-blog-settings-card">
            <h4 className="db-blog-settings-title">Post Settings</h4>

            <div className="db-blog-field-group">
              <label className="db-label">Status</label>
              <div className="db-blog-status-row">
                {['draft', 'published'].map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`db-blog-status-btn${editingBlog.status === s ? ' db-blog-status-btn--active db-blog-status-btn--' + s : ''}`}
                    onClick={() => updateBlogForm('status', s)}
                  >
                    {s === 'published' ? '● Published' : '○ Draft'}
                  </button>
                ))}
              </div>
            </div>

            {editingBlog.status === 'published' && (
              <div className="db-blog-field-group">
                <label className="db-label">Publish Date</label>
                <input
                  className="db-input"
                  type="datetime-local"
                  value={editingBlog.published_at ? editingBlog.published_at.slice(0, 16) : ''}
                  onChange={e => updateBlogForm('published_at', e.target.value)}
                />
              </div>
            )}

            <div className="db-blog-field-group">
              <label className="db-label">Category</label>
              <input
                className="db-input"
                type="text"
                placeholder="e.g. Shopify, Ecommerce, SEO…"
                value={editingBlog.category}
                onChange={e => updateBlogForm('category', e.target.value)}
              />
            </div>

            <div className="db-blog-field-group">
              <label className="db-label">Category Color</label>
              <div className="db-blog-color-row">
                <input
                  type="color"
                  className="db-blog-color-picker"
                  value={editingBlog.category_color || '#4f46e5'}
                  onChange={e => updateBlogForm('category_color', e.target.value)}
                />
                <input
                  className="db-input"
                  type="text"
                  value={editingBlog.category_color || '#4f46e5'}
                  onChange={e => updateBlogForm('category_color', e.target.value)}
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
                {editingBlog.category && (
                  <span className="db-blog-cat-preview" style={{ background: editingBlog.category_color || '#4f46e5' }}>
                    {editingBlog.category}
                  </span>
                )}
              </div>
              <div className="db-blog-color-presets">
                {['#4f46e5','#96bf48','#ff9900','#e63946','#0891b2','#7c3aed','#059669','#dc2626'].map(c => (
                  <button key={c} type="button" className="db-blog-color-dot"
                    style={{ background: c, outline: editingBlog.category_color === c ? '2px solid #e6edf3' : 'none' }}
                    onClick={() => updateBlogForm('category_color', c)} />
                ))}
              </div>
            </div>

            <div className="db-blog-field-group">
              <label className="db-label">Author</label>
              <input
                className="db-input"
                type="text"
                placeholder="Author name"
                value={editingBlog.author}
                onChange={e => updateBlogForm('author', e.target.value)}
              />
            </div>

            <div className="db-blog-field-group">
              <label className="db-label">Read Time</label>
              <input
                className="db-input"
                type="text"
                placeholder="e.g. 7 min read"
                value={editingBlog.read_time}
                onChange={e => updateBlogForm('read_time', e.target.value)}
              />
              <p className="db-blog-field-hint">Auto-updated as you write.</p>
            </div>

            {editingBlog.id && (
              <button
                type="button"
                className="db-btn db-btn--delete db-btn--sm"
                style={{ width: '100%', marginTop: 8 }}
                onClick={() => deleteBlog(editingBlog.id, editingBlog.title)}
              >
                Delete Post
              </button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );

  const blogsPage = (
    <div className="db-table-page">
      {editingBlog === null ? blogListView : blogEditorView}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  OVERVIEW PAGE
  // ─────────────────────────────────────────────────────────────────────────
  const overviewPage = (
    <div className="db-overview">
      <div className="db-page-header">
        <h2 className="db-page-title">Overview</h2>
        <button className="db-btn db-btn--ghost" onClick={fetchAll} disabled={loading}>
          <IcoRefresh /> {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loading && <div className="db-loading"><span className="db-spinner" /> Loading…</div>}

      {stats && !loading && (
        <>
          <div className="db-stat-grid">
            <div className="db-stat-card db-stat-card--clickable" onClick={() => switchView('contacts')}>
              <span className="db-stat-num">{stats.contacts}</span>
              <span className="db-stat-label">Total Contacts</span>
              <span className="db-stat-sub">+{stats.new_contacts_today ?? 0} today · +{stats.new_contacts_week ?? 0} this week</span>
            </div>
            <div className="db-stat-card db-stat-card--clickable" onClick={() => switchView('applications')}>
              <span className="db-stat-num">{stats.applications}</span>
              <span className="db-stat-label">Applications</span>
              <span className="db-stat-sub">+{stats.new_apps_today ?? 0} today · +{stats.new_apps_week ?? 0} this week</span>
            </div>
            <div className="db-stat-card db-stat-card--clickable" onClick={() => switchView('leads')}>
              <span className="db-stat-num">{stats.leads}</span>
              <span className="db-stat-label">Popup Leads</span>
              <span className="db-stat-sub">+{stats.new_leads_today ?? 0} today · +{stats.new_leads_week ?? 0} this week</span>
            </div>
            <div className="db-stat-card">
              <span className="db-stat-num db-stat-num--accent">{stats.resumes}</span>
              <span className="db-stat-label">Resumes on File</span>
              <span className="db-stat-sub">across all applications</span>
            </div>
          </div>

          <div className="db-recent-grid">
            <div className="db-recent-card">
              <div className="db-recent-header">
                <span className="db-recent-title">Recent Contacts</span>
                <button className="db-btn db-btn--ghost db-btn--xs" onClick={() => switchView('contacts')}>View all</button>
              </div>
              {contacts.slice(0, 5).map(r => (
                <div key={r.id} className="db-recent-row">
                  <div>
                    <span className="db-recent-name">{r.name}</span>
                    <span className="db-recent-meta">{r.email}</span>
                  </div>
                  <div className="db-recent-right">
                    <span className={`db-status-pill db-status--${r.status || 'new'}`}>{r.status || 'new'}</span>
                    <span className="db-recent-date">{fmtDate(r.created_at)}</span>
                  </div>
                </div>
              ))}
              {contacts.length === 0 && <p className="db-recent-empty">No contacts yet.</p>}
            </div>

            <div className="db-recent-card">
              <div className="db-recent-header">
                <span className="db-recent-title">Recent Applications</span>
                <button className="db-btn db-btn--ghost db-btn--xs" onClick={() => switchView('applications')}>View all</button>
              </div>
              {applications.slice(0, 5).map(r => (
                <div key={r.id} className="db-recent-row">
                  <div>
                    <span className="db-recent-name">{r.name}</span>
                    <span className="db-recent-meta">{r.department} · {r.role}</span>
                  </div>
                  <div className="db-recent-right">
                    <span className={`db-status-pill db-status--${r.status || 'new'}`}>{r.status || 'new'}</span>
                    <span className="db-recent-date">{fmtDate(r.created_at)}</span>
                  </div>
                </div>
              ))}
              {applications.length === 0 && <p className="db-recent-empty">No applications yet.</p>}
            </div>

            <div className="db-recent-card">
              <div className="db-recent-header">
                <span className="db-recent-title">Recent Popup Leads</span>
                <button className="db-btn db-btn--ghost db-btn--xs" onClick={() => switchView('leads')}>View all</button>
              </div>
              {leads.slice(0, 5).map(r => (
                <div key={r.id} className="db-recent-row">
                  <div>
                    <span className="db-recent-name">{r.phone}</span>
                    <span className="db-recent-meta">{r.email}</span>
                  </div>
                  <div className="db-recent-right">
                    <span className={`db-status-pill db-status--${r.status || 'new'}`}>{r.status || 'new'}</span>
                    <span className="db-recent-date">{fmtDate(r.created_at)}</span>
                  </div>
                </div>
              ))}
              {leads.length === 0 && <p className="db-recent-empty">No popup leads yet.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  TABLE TOOLBAR (shared)
  // ─────────────────────────────────────────────────────────────────────────
  const tableToolbar = (
    <div className="db-toolbar">
      <div className="db-toolbar-left">
        <div className="db-search-wrap">
          <svg className="db-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input className="db-search" type="search"
            placeholder={`Search ${view}…`}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {view === 'applications' && (
          <select className="db-filter-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="">All Departments</option>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
          </select>
        )}

        <select className="db-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {activeStatuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>

        <input className="db-date-input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
        <span className="db-date-sep">→</span>
        <input className="db-date-input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />

        {hasFilters && <button className="db-btn db-btn--ghost db-btn--xs" onClick={clearFilters}>Clear</button>}
      </div>
      <div className="db-toolbar-right">
        <span className="db-count">{currentData.length} records</span>
        <button className="db-btn db-btn--ghost db-btn--xs" onClick={handleExport} disabled={currentData.length === 0}>↓ CSV</button>
      </div>
    </div>
  );

  // ── Bulk bar ──────────────────────────────────────────────────────────────
  const bulkBar = selected.size > 0 && (
    <div className="db-bulk-bar">
      <span className="db-bulk-info">{selected.size} selected</span>
      {view !== 'leads' ? (
        <>
          <select className="db-filter-select" value="" disabled={bulkWorking}
            onChange={e => { if (e.target.value) handleBulkStatus(e.target.value); }}>
            <option value="">Mark as…</option>
            {activeStatuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <button className="db-btn db-btn--delete db-btn--xs" disabled={bulkWorking} onClick={handleBulkDelete}>
            {bulkWorking ? 'Working…' : `Delete ${selected.size}`}
          </button>
        </>
      ) : (
        <>
          <select className="db-filter-select" value="" disabled={bulkWorking}
            onChange={e => { if (e.target.value) handleBulkLeads('status', e.target.value); }}>
            <option value="">Mark as…</option>
            {LEAD_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <button className="db-btn db-btn--delete db-btn--xs" disabled={bulkWorking} onClick={() => handleBulkLeads('delete')}>
            {bulkWorking ? 'Working…' : `Delete ${selected.size}`}
          </button>
        </>
      )}
      <button className="db-btn db-btn--ghost db-btn--xs" onClick={() => setSelected(new Set())}>Deselect</button>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  CONTACTS TABLE
  // ─────────────────────────────────────────────────────────────────────────
  const contactsPage = (
    <div className="db-table-page">
      <div className="db-page-header">
        <h2 className="db-page-title">Contacts <span className="db-page-count">{contacts.length}</span></h2>
        <button className="db-btn db-btn--ghost" onClick={fetchAll} disabled={loading}><IcoRefresh /> Refresh</button>
      </div>
      {tableToolbar}
      {bulkBar}
      {error && <div className="db-error-banner">{error}</div>}
      {loading
        ? <div className="db-loading"><span className="db-spinner" /> Loading…</div>
        : filteredContacts.length === 0
          ? <div className="db-empty">No contacts yet{hasFilters ? ' matching your filters' : ''}.</div>
          : (
            <div className="db-table-wrap">
              <table className="db-table">
                <thead>
                  <tr>
                    <th className="db-th--checkbox">
                      <input type="checkbox" className="db-checkbox" checked={allSelected}
                        onChange={e => { if (e.target.checked) setSelected(new Set(filteredContacts.map(r => r.id))); else setSelected(new Set()); }} />
                    </th>
                    <th className="db-th--num">#</th>
                    <SortTh field="name"       config={sortConfig} onSort={handleSort}>Name</SortTh>
                    <SortTh field="email"      config={sortConfig} onSort={handleSort}>Email</SortTh>
                    <SortTh field="phone"      config={sortConfig} onSort={handleSort}>Phone</SortTh>
                    <SortTh field="service"    config={sortConfig} onSort={handleSort}>Service</SortTh>
                    <SortTh field="budget"     config={sortConfig} onSort={handleSort}>Budget</SortTh>
                    <SortTh field="status"     config={sortConfig} onSort={handleSort}>Status</SortTh>
                    <SortTh field="created_at" config={sortConfig} onSort={handleSort}>Date</SortTh>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map(r => (
                    <Fragment key={r.id}>
                      <tr className={`db-row${expanded === r.id ? ' db-row--open' : ''}${selected.has(r.id) ? ' db-row--selected' : ''}`}
                        onClick={() => toggleRow('contact', r.id)}>
                        <td className="db-td--checkbox" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" className="db-checkbox" checked={selected.has(r.id)}
                            onChange={() => setSelected(p => { const n = new Set(p); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} />
                        </td>
                        <td className="db-td--num">{r.id}</td>
                        <td className="db-td--name">{r.name}</td>
                        <td><a href={`mailto:${r.email}`} className="db-link" onClick={e => e.stopPropagation()}>{r.email}</a></td>
                        <td>{r.phone || <span className="db-na">—</span>}</td>
                        <td>{r.service || <span className="db-na">—</span>}</td>
                        <td>{r.budget || <span className="db-na">—</span>}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <select className={`db-status-select db-status--${r.status || 'new'}`}
                            value={r.status || 'new'} disabled={updatingStatus === r.id}
                            onChange={e => handleStatusChange('contact', r.id, e.target.value)}>
                            {CONTACT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                          </select>
                        </td>
                        <td className="db-td--date">{fmtDate(r.created_at)}</td>
                        <td><span className="db-peek">{r.message?.length > 60 ? r.message.slice(0, 60) + '…' : r.message}</span></td>
                      </tr>
                      {expanded === r.id && (
                        <tr className="db-row-detail">
                          <td colSpan={10}>
                            <div className="db-detail-box">
                              <p className="db-detail-field"><strong>Full Message:</strong>{r.message}</p>
                              {r.referral && <p className="db-detail-field"><strong>Found us via:</strong> {r.referral}</p>}
                              <p className="db-detail-meta">ID {r.id} · IP: {r.ip_address || '—'} · Submitted: {fmtDateTime(r.created_at)}</p>
                              <div className="db-detail-actions">
                                <a href={`mailto:${r.email}`} className="db-btn db-btn--ghost db-btn--xs">✉ Email {r.name}</a>
                                {r.phone && (
                                  <a href={`https://wa.me/${r.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="db-btn db-btn--ghost db-btn--xs">WhatsApp</a>
                                )}
                                <button className="db-btn db-btn--delete db-btn--xs" disabled={deleting === r.id}
                                  onClick={e => { e.stopPropagation(); handleDelete('contact', r.id, r.name); }}>
                                  {deleting === r.id ? 'Deleting…' : 'Delete Record'}
                                </button>
                              </div>
                              {renderNotes('contact', r)}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  APPLICATIONS TABLE
  // ─────────────────────────────────────────────────────────────────────────
  const applicationsPage = (
    <div className="db-table-page">
      <div className="db-page-header">
        <h2 className="db-page-title">Applications <span className="db-page-count">{applications.length}</span></h2>
        <button className="db-btn db-btn--ghost" onClick={fetchAll} disabled={loading}><IcoRefresh /> Refresh</button>
      </div>
      {tableToolbar}
      {bulkBar}
      {error && <div className="db-error-banner">{error}</div>}
      {loading
        ? <div className="db-loading"><span className="db-spinner" /> Loading…</div>
        : filteredApps.length === 0
          ? <div className="db-empty">No applications yet{hasFilters ? ' matching your filters' : ''}.</div>
          : (
            <div className="db-table-wrap">
              <table className="db-table">
                <thead>
                  <tr>
                    <th className="db-th--checkbox">
                      <input type="checkbox" className="db-checkbox" checked={allSelected}
                        onChange={e => { if (e.target.checked) setSelected(new Set(filteredApps.map(r => r.id))); else setSelected(new Set()); }} />
                    </th>
                    <th className="db-th--num">#</th>
                    <SortTh field="name"       config={sortConfig} onSort={handleSort}>Name</SortTh>
                    <SortTh field="email"      config={sortConfig} onSort={handleSort}>Email / WhatsApp</SortTh>
                    <SortTh field="role"       config={sortConfig} onSort={handleSort}>Role</SortTh>
                    <SortTh field="department" config={sortConfig} onSort={handleSort}>Dept</SortTh>
                    <SortTh field="experience" config={sortConfig} onSort={handleSort}>Experience</SortTh>
                    <SortTh field="status"     config={sortConfig} onSort={handleSort}>Status</SortTh>
                    <SortTh field="created_at" config={sortConfig} onSort={handleSort}>Date</SortTh>
                    <th>Resume</th>
                    <th>Cover Letter</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map(r => (
                    <Fragment key={r.id}>
                      <tr className={`db-row${expanded === r.id ? ' db-row--open' : ''}${selected.has(r.id) ? ' db-row--selected' : ''}`}
                        onClick={() => toggleRow('application', r.id)}>
                        <td className="db-td--checkbox" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" className="db-checkbox" checked={selected.has(r.id)}
                            onChange={() => setSelected(p => { const n = new Set(p); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} />
                        </td>
                        <td className="db-td--num">{r.id}</td>
                        <td className="db-td--name">{r.name}</td>
                        <td>
                          <a href={`mailto:${r.email}`} className="db-link" onClick={e => e.stopPropagation()}>{r.email}</a>
                          {r.whatsapp && <span className="db-sub">{r.whatsapp}</span>}
                        </td>
                        <td>{r.role || <span className="db-na">—</span>}</td>
                        <td>
                          {r.department
                            ? <span className={`db-dept db-dept--${r.department.toLowerCase()}`}>{r.department}</span>
                            : <span className="db-na">—</span>}
                        </td>
                        <td>{r.experience || <span className="db-na">—</span>}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <select className={`db-status-select db-status--${r.status || 'new'}`}
                            value={r.status || 'new'} disabled={updatingStatus === r.id}
                            onChange={e => handleStatusChange('application', r.id, e.target.value)}>
                            {APP_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                          </select>
                        </td>
                        <td className="db-td--date">{fmtDate(r.created_at)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          {r.resume_path
                            ? <a href={`${API}?action=download&id=${r.id}&token=${token}`} className="db-btn db-btn--download" download>↓ Resume</a>
                            : <span className="db-na">—</span>}
                        </td>
                        <td><span className="db-peek">{r.message?.length > 60 ? r.message.slice(0, 60) + '…' : r.message}</span></td>
                      </tr>
                      {expanded === r.id && (
                        <tr className="db-row-detail">
                          <td colSpan={11}>
                            <div className="db-detail-box">
                              <p className="db-detail-field"><strong>Cover Letter:</strong>{r.message}</p>
                              {r.portfolio && <p className="db-detail-field"><strong>Portfolio:</strong> <a href={r.portfolio} target="_blank" rel="noopener noreferrer" className="db-link">{r.portfolio}</a></p>}
                              <p className="db-detail-meta">ID {r.id} · IP: {r.ip_address || '—'} · Submitted: {fmtDateTime(r.created_at)}</p>
                              <div className="db-detail-actions">
                                <a href={`mailto:${r.email}`} className="db-btn db-btn--ghost db-btn--xs">✉ Email {r.name}</a>
                                {r.whatsapp && (
                                  <a href={`https://wa.me/${r.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="db-btn db-btn--ghost db-btn--xs">WhatsApp</a>
                                )}
                                {r.resume_path && (
                                  <a href={`${API}?action=download&id=${r.id}&token=${token}`} className="db-btn db-btn--download" download>↓ Download Resume</a>
                                )}
                                <button className="db-btn db-btn--delete db-btn--xs" disabled={deleting === r.id}
                                  onClick={e => { e.stopPropagation(); handleDelete('application', r.id, r.name); }}>
                                  {deleting === r.id ? 'Deleting…' : 'Delete Record'}
                                </button>
                              </div>
                              {renderNotes('application', r)}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  POPUP LEADS TABLE
  // ─────────────────────────────────────────────────────────────────────────
  const leadsPage = (
    <div className="db-table-page">
      <div className="db-page-header">
        <h2 className="db-page-title">Popup Leads <span className="db-page-count">{leads.length}</span></h2>
        <button className="db-btn db-btn--ghost" onClick={fetchAll} disabled={loading}><IcoRefresh /> Refresh</button>
      </div>
      {tableToolbar}
      {bulkBar}
      {error && <div className="db-error-banner">{error}</div>}
      {loading
        ? <div className="db-loading"><span className="db-spinner" /> Loading…</div>
        : filteredLeads.length === 0
          ? <div className="db-empty">No popup leads yet{hasFilters ? ' matching your filters' : ''}.</div>
          : (
            <div className="db-table-wrap">
              <table className="db-table">
                <thead>
                  <tr>
                    <th className="db-th--checkbox">
                      <input type="checkbox" className="db-checkbox" checked={allSelected}
                        onChange={e => { if (e.target.checked) setSelected(new Set(filteredLeads.map(r => r.id))); else setSelected(new Set()); }} />
                    </th>
                    <th className="db-th--num">#</th>
                    <SortTh field="phone"      config={sortConfig} onSort={handleSort}>Phone</SortTh>
                    <SortTh field="email"      config={sortConfig} onSort={handleSort}>Email</SortTh>
                    <SortTh field="status"     config={sortConfig} onSort={handleSort}>Status</SortTh>
                    <SortTh field="created_at" config={sortConfig} onSort={handleSort}>Date</SortTh>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(r => (
                    <Fragment key={r.id}>
                      <tr className={`db-row${expanded === r.id ? ' db-row--open' : ''}${selected.has(r.id) ? ' db-row--selected' : ''}`}
                        onClick={() => toggleRow('lead', r.id)}>
                        <td className="db-td--checkbox" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" className="db-checkbox" checked={selected.has(r.id)}
                            onChange={() => setSelected(p => { const n = new Set(p); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} />
                        </td>
                        <td className="db-td--num">{r.id}</td>
                        <td className="db-td--name">{r.phone}</td>
                        <td><a href={`mailto:${r.email}`} className="db-link" onClick={e => e.stopPropagation()}>{r.email}</a></td>
                        <td onClick={e => e.stopPropagation()}>
                          <select className={`db-status-select db-status--${r.status || 'new'}`}
                            value={r.status || 'new'} disabled={updatingStatus === r.id}
                            onChange={e => handleLeadStatusChange(r.id, e.target.value)}>
                            {LEAD_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                          </select>
                        </td>
                        <td className="db-td--date">{fmtDate(r.created_at)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <a href={`mailto:${r.email}`} className="db-btn db-btn--ghost db-btn--xs">✉</a>
                            <a href={`https://wa.me/${r.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="db-btn db-btn--ghost db-btn--xs">WA</a>
                            <button className="db-btn db-btn--delete db-btn--xs" disabled={deleting === r.id}
                              onClick={() => handleDeleteLead(r.id)}>
                              {deleting === r.id ? '…' : '✕'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expanded === r.id && (
                        <tr className="db-row-detail">
                          <td colSpan={7}>
                            <div className="db-detail-box">
                              <p className="db-detail-field"><strong>Phone:</strong> {r.phone}</p>
                              <p className="db-detail-field"><strong>Email:</strong> {r.email}</p>
                              <p className="db-detail-meta">ID {r.id} · IP: {r.ip_address || '—'} · Submitted: {fmtDateTime(r.created_at)}</p>
                              <div className="db-detail-actions">
                                <a href={`mailto:${r.email}`} className="db-btn db-btn--ghost db-btn--xs">✉ Email</a>
                                <a href={`https://wa.me/${r.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="db-btn db-btn--ghost db-btn--xs">WhatsApp</a>
                                <button className="db-btn db-btn--delete db-btn--xs" disabled={deleting === r.id}
                                  onClick={() => handleDeleteLead(r.id)}>
                                  {deleting === r.id ? 'Deleting…' : 'Delete Lead'}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  //  MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="db-root">
      {changePwdModal}

      {/* Mobile overlay */}
      {sidebarOpen && <div className="db-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* ── Sidebar ── */}
      <aside className={`db-sidebar${sidebarOpen ? ' db-sidebar--open' : ''}`}>
        <div className="db-sidebar-brand">
          <span className="db-brand-dot" />
          <span className="db-sidebar-brand-text">MMC Admin</span>
        </div>

        <nav className="db-sidebar-nav">
          {navItems.map(item => (
            <button key={item.id}
              className={`db-nav-item${view === item.id ? ' db-nav-item--active' : ''}`}
              onClick={() => switchView(item.id)}>
              <span className="db-nav-icon">{item.icon}</span>
              <span className="db-nav-label">{item.label}</span>
              {item.badge != null && <span className="db-nav-badge">{item.badge}</span>}
            </button>
          ))}
        </nav>

        <div className="db-sidebar-footer">
          <div className="db-user-chip">
            <span className="db-user-dot" />
            {adminUser}
          </div>
          <button className="db-nav-action" onClick={() => setShowChangePwd(true)}>Change Password</button>
          <button className="db-nav-action db-nav-action--danger" onClick={handleLogout}>Sign Out</button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="db-main">
        {/* Mobile topbar */}
        <div className="db-topbar">
          <button className="db-topbar-toggle" onClick={() => setSidebarOpen(p => !p)} aria-label="Toggle menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <span className="db-topbar-title">
            {navItems.find(n => n.id === view)?.label ?? 'Dashboard'}
          </span>
          <div className="db-topbar-right">
            <button className="db-btn db-btn--ghost db-btn--xs" onClick={fetchAll} disabled={loading}>
              <IcoRefresh />
            </button>
          </div>
        </div>

        <div className="db-main-content">
          {view === 'overview'      && overviewPage}
          {view === 'contacts'      && contactsPage}
          {view === 'applications'  && applicationsPage}
          {view === 'leads'         && leadsPage}
          {view === 'blogs'         && blogsPage}
        </div>
      </div>
    </div>
  );
}
